import { format, isValid } from 'date-fns'
import { useSelector } from 'react-redux'
import { useMemo, useRef, useState, useEffect, useCallback } from 'react'
import { Grid, Box } from '@mui/material'
import { localeDateFormat } from '../../../utils/localeUtils'
import { RootStore, useAppDispatch } from '../../../redux/store'
import Alert from '../../../shared/UI/Alert/Alert'
import Paragraph from '../../../shared/UI/Paragraph'
import AbsenceSummary from './AbsenceSummary'
import EmployeePicker from './EmployeePicker'
import SubmitActionFooter from './SubmitActionsFooter'
import AbsenceTimeFramePicker from './AbsenceTimeFramePicker'
import AbsenceTypeSelection from './AbsenceTypeSelection'
import { SwitchContainer } from '../components'
import CheckBox from '../../../shared/UI/CheckBox'
import ContactMade from './ContactMade'
import AbsenceComments from './AbsenceComments'
import useAbsenceDateRange from './Hooks/useAbsenceDateRange'
import useAbsenceEmployee from './Hooks/useAbsenceEmployee'
import useAbsenceReasons from './Hooks/useAbsenceReasons'
import useAbsenceDrawerAlerts from './Hooks/useAbsenceDrawerAlerts'
import useShiftOnDay from './Hooks/useShiftOnDay'
import useWfmAbsence from './Hooks/useWfmAbsence'
import usePartDayAbsenceLogic from './Hooks/usePartDayAbsenceLogic'
import useAbsenceComments from './Hooks/useAbsenceComments'
import useAbsenceContact from './Hooks/useAbsenceContact'
import useWfmAbsenceTypeAndReason from './Hooks/useWfmAbsenceReasons'
import {
  AbsenceCreateRequest,
  AbsenceDayRequestStr,
  WFMAbsenceActionedPayload,
} from '../../../types/absence'
import { DateFormats } from '../../../api/absence/tempTypes/generalprops'
import PartDayAbsenceForm from './PartDayAbsenceForm'
import { requestsService } from '../../../services/requestsService'
import actionsService from '../../../services/actionsService'
import { showErrorMessage, showSuccessMessage } from '../../../redux/reducers/snackbarReducer'
import UserErrorMessage from '../../../utils/errorFilter'
import { hideDrawer, hideHeaderDrawer } from '../../../redux/reducers/appSettingsReducer'
import { BaseResponse } from '../../../types/base-response'
import { setSelectedDays } from '../../../redux/reducers/timeOffRequestsReducer'
import ConfirmationCard, { ConfirmationRow } from '../ConfirmationCard'
import { manualRequestsService } from '../../../services/myActionsService'
import { settingsService } from '../../../services/settingsService'
import { AbsenceLocation } from '../../../types/absence-location'
import { AbsenceReasonTypesEnum, AbsenceTypesEnum, nonSicknessTypesWithInfoBanner } from '../../Absence/types'
import {
  calculateManAuthDays,
  getOSPForAbsenceType,
  buildPayApprovalText,
} from '../utils'
import { featureToggleKeys } from '../../../types/feature-toggles'
import { useManAuth } from '../../Absence/Actions/RTW/UseManAuth'
import { useDepartmentSettings } from '../../../utils/Hooks/useDepartmentSettings'
import { useOSPCalculations } from '../hooks/useOSPCalculations'
import { useManAuthValidation } from '../hooks/useManAuthValidation'
import { useSickNoteReminder } from '../hooks/useSickNoteReminder'
import OSPEntitlementCard from '../ManAuthOspEntitlementCard'
import OSPNotificationWithApproval from '../ManAuthPayApprovalBanner'
import NumberInput from '../../../shared/UI/NumberInput'
import InfoBanner from '../../../shared/UI/InfoBanner'
import { absenceFormLabels } from '../../../utils/constants'
import { isValidPlannerDate } from '../../../utils/date-utils'

export const DiscretionaryPayReason = [
  AbsenceReasonTypesEnum.PartnerChildDependantNotWell,
  AbsenceReasonTypesEnum.Compassionate,
  AbsenceReasonTypesEnum.Other,
]

interface AbsenceCreationProps {
  employeeId?: number
  wfmAbsenceId?: number
  refreshActions: () => Promise<void>
}

const componentIds = {
  none: 0,
  employeeSelection: 1,
  absenceDates: 2,
  shiftString: 3,
  expectedReturn: 4,
  partDayAbsenceFrom: 5,
  absenceType: 6,
  absenceReason: 7,
  contactMadeBy: 8,
  contactedDate: 9,
  comments: 10
}

function WfmAbsenceActionDrawer({
  employeeId,
  wfmAbsenceId,
  refreshActions,
}: AbsenceCreationProps) {
  const dispatch = useAppDispatch()
  const gridContainerRef = useRef(null)

  const { directReports } = useSelector((state: RootStore) => state.appSettings)
  const { ukPlannerDayforceAbsence } = useSelector((state: RootStore) => state.featureToggles)
  const { selectedDays } = useSelector((state: RootStore) => state.timeOff)
  const { wfmAbsence, unlockWfmAbsence } = useWfmAbsence(wfmAbsenceId)

  const {
    alertMessage,
    setAlertMessage,
    alertComponentId,
    setAlertComponentId,
    hasConflict,
    setHasConflict,
  } = useAbsenceDrawerAlerts()

  const {
    shiftString,
    setShiftString,
    disableShiftStringEditing,
    setDisableShiftStringEditing,
    calculatePartDayAbsenceForWfm,
  } = useShiftOnDay(wfmAbsence)

  const { selectedEmployee, setSelectedEmployee, departmentWorksWeekends } =
    useAbsenceEmployee(employeeId)

  const { absenceDates, setAbsenceDates, disableEditing, expectedReturn, setExpectedReturn, dateRangeError, setDateRangeError, expectedReturnError, setExpectedReturnError } =
    useAbsenceDateRange(departmentWorksWeekends, wfmAbsence)

  const {
    absenceReason,
    setAbsenceReason,
    absenceType,
    setAbsenceType,
    absenceReasonTypeList,
    absenceTypesList,
    employeeAbsenceTypesList,
    covidIndicator,
    setCovidIndicator,
  } = useAbsenceReasons(selectedEmployee)

  const { partDayAbsence, setPartDayAbsence, partDayAbsentFrom, setPartDayAbsentFrom } =
    usePartDayAbsenceLogic(wfmAbsence)

  const { comments, setComments, contactComments, setContactComments, wfmComments } =
    useAbsenceComments(wfmAbsence)

  const { contactMadeBy, setContactMadeBy, contactDateTime, setContactDateTime } =
    useAbsenceContact()

  const [hasHadLunch, setHasHadLunch] = useState<boolean>(false)
  const [toWorkBack, setToWorkBack] = useState<boolean>(false)
  const [isWorkBackConfirmed, setIsWorkBackConfirmed] = useState<boolean>(false)
  const [isSickNoteConfirmed, setIsSickNoteConfirmed] = useState<boolean>(false)

  const workBackEligibleLocations = useMemo(() => new Set(['Stoke', 'Manchester', 'Remote UK']), [])
  const [employeeAbsenceLocations, setEmployeeAbsenceLocations] = useState<AbsenceLocation[]>([])
  const [isLoadingEmployeeAbsenceLocations, setIsLoadingEmployeeAbsenceLocations] = useState<boolean>(false)
  const [sendFirstDayNotification, setSendFirstDayNotification] = useState<boolean>(true)
  const userInfo = useSelector((state: RootStore) => state.userState.loggedInUser)
  const [hasFutureSicknessAbsence, setHasFutureSicknessAbsence] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  // Department settings and man auth hooks
  const departmentSettings = useDepartmentSettings(selectedEmployee?.departmentId)
  const { isManAuthOn } = useManAuth(featureToggleKeys.ManAuthUK, departmentSettings)

  // Use OSP calculations hook
  const {
    remainingOSP,
    totalSickPayAllowanceHours,
    isLoadingOSP,
    dailyOSP,
    totalAvailableOSP,
    totalRequestedHours,
    exceededHours,
    exceededHoursPerDay,
  } = useOSPCalculations({
    employeeId: selectedEmployee?.employeeId,
    dateRange: absenceDates,
    selectedDays,
    absenceCategoryId: absenceReason?.value,
  })

  // Use man auth validation hook
  const {
    isPayApprovalConfirmed,
    managerApprovedHours,
    hasValidHoursInput,
    isManAuthRequest,
    isManAuthValid,
    handleApprovalHoursChange,
    handlePayApprovalChange,
  } = useManAuthValidation({
    isManAuthOn,
    absenceReasonValue: absenceReason?.value,
    absenceTypeValue: absenceType?.value,
    exceededHours,
    toWorkBack,
    discretionaryPayReasons: DiscretionaryPayReason,
  })

  useEffect(() => {
    if (!employeeId || !absenceDates[1] || !isValid(absenceDates[1])) {
      return
    }

    manualRequestsService
      .hasFutureSicknessAbsence(employeeId, absenceDates[1])
      .then(data => {
        setHasFutureSicknessAbsence(data.hasFutureSicknessAbsence)
      })
      .catch(err => {
        const response: BaseResponse = err.response.data
        response.errors.forEach(error => {
          dispatch(showErrorMessage(<UserErrorMessage name={error.name} />))
        })
      })
  }, [employeeId, absenceDates[1]])

  useEffect(() => unlockWfmAbsence, [])

  useWfmAbsenceTypeAndReason({
    wfmAbsence,
    absenceTypesList,
    absenceReasonTypeList,
    setAbsenceType,
    setAbsenceReason,
  })

  useEffect(() => {
    setIsLoadingEmployeeAbsenceLocations(true)
    settingsService
      .getLocation()
      .then(res => {
        setEmployeeAbsenceLocations(res.absenceLocations || [])
      })
      .catch(() => {
        setEmployeeAbsenceLocations([])
      })
      .finally(() => setIsLoadingEmployeeAbsenceLocations(false))
  }, [])

  const selectedEmployeeLocationResolvedName = useMemo(() => {
    if (!selectedEmployee?.locationId) return undefined
    return employeeAbsenceLocations.find(l => l.locationId === selectedEmployee.locationId)?.name
  }, [employeeAbsenceLocations, selectedEmployee?.locationId])

  const isWorkBackLocationEligible = useMemo(() => {
    if (!selectedEmployeeLocationResolvedName) return false
    return workBackEligibleLocations.has(selectedEmployeeLocationResolvedName)
  }, [workBackEligibleLocations, selectedEmployeeLocationResolvedName])

  const shouldShowWorkBackConfirmation =
    ukPlannerDayforceAbsence && toWorkBack && isWorkBackLocationEligible && !isLoadingEmployeeAbsenceLocations

  useEffect(() => {
    if (!ukPlannerDayforceAbsence) {
      return
    }

    if (!toWorkBack && isWorkBackConfirmed) {
      setIsWorkBackConfirmed(false)
    }

    if (!shouldShowWorkBackConfirmation && isWorkBackConfirmed) {
      setIsWorkBackConfirmed(false)
    }
  }, [isWorkBackConfirmed, shouldShowWorkBackConfirmation, toWorkBack, ukPlannerDayforceAbsence])

  const handleWorkBackConfirmationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsWorkBackConfirmed(event.target.checked)
  }

  const handleSickNoteConfirmationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsSickNoteConfirmed(event.target.checked)
  }

  useEffect(() => {
    if (!selectedDays || selectedDays.length === 0 || !partDayAbsence) {
      return
    }

    const updatedSelectedDays = selectedDays.map(day => ({
      ...day,
      hours: hasHadLunch ? day.hours + 0.5 : Math.max(0, day.hours - 0.5),
    }))

    dispatch(setSelectedDays(updatedSelectedDays))
  }, [hasHadLunch])

  const isComponentWithError = useCallback(
    (componentId: number): boolean => alertComponentId === componentId,
    [alertComponentId]
  )

  // Check if we should show non-sickness OSP card
  const showNonSicknessOspCard =
    isManAuthOn &&
    absenceType?.value !== 0 &&
    nonSicknessTypesWithInfoBanner.includes(absenceType?.value || 0) &&
    !toWorkBack

  // Check if this is a sickness absence
  const isSickness = absenceReason?.value === AbsenceTypesEnum.Sickness

  const { shouldShowSickNoteReminder } = useSickNoteReminder({
    selectedDays,
    isSickness,
    isUKEmployee: isWorkBackLocationEligible,
  })

  useEffect(() => {
    if (!shouldShowSickNoteReminder && isSickNoteConfirmed) {
      setIsSickNoteConfirmed(false)
    }
  }, [isSickNoteConfirmed, shouldShowSickNoteReminder])

  const payApprovalContent = useMemo(() => {
    const displayHours = managerApprovedHours !== undefined ? managerApprovedHours : exceededHours ?? 0
    return buildPayApprovalText(displayHours)
  }, [managerApprovedHours, exceededHours])

  const confirmations = useMemo<ConfirmationRow[]>(() => {
    const rows: ConfirmationRow[] = []
    if (shouldShowWorkBackConfirmation) {
      rows.push({
        key: 'work-back',
        content: absenceFormLabels.workBackConfirmationText,
        checked: isWorkBackConfirmed,
        onChange: handleWorkBackConfirmationChange,
        dataTestId: 'MyActions-Absence-WorkBack-Checkbox',
      })
    }
    if (isManAuthValid && isManAuthRequest) {
      rows.push({
        key: 'pay-approval',
        content: payApprovalContent,
        checked: isPayApprovalConfirmed,
        onChange: handlePayApprovalChange,
        dataTestId: 'MyActions-Absence-PayApproval-Checkbox',
      })
    }
    if (shouldShowSickNoteReminder) {
      rows.push({
        key: 'sick-note',
        content: absenceFormLabels.SickNoteReminderText,
        checked: isSickNoteConfirmed,
        onChange: handleSickNoteConfirmationChange,
        dataTestId: 'MyActions-Absence-SickNote-Checkbox',
      })
    }
    return rows
  }, [
    shouldShowWorkBackConfirmation,
    isWorkBackConfirmed,
    isManAuthValid,
    isManAuthRequest,
    isPayApprovalConfirmed,
    handlePayApprovalChange,
    payApprovalContent,
    shouldShowSickNoteReminder,
    isSickNoteConfirmed,
  ])
  const isNonSicknessOrNoShow =
    absenceReason?.value === AbsenceTypesEnum.NonSickness ||
    absenceReason?.value === 3 // NoShow

  const formAlertMessage = useMemo((): string | undefined => {
    if (!selectedEmployee) {
      setAlertComponentId(componentIds.employeeSelection)
      return 'Employee is required'
    }

    if (dateRangeError !== null && (dateRangeError[0] || dateRangeError[1])) {
      setAlertComponentId(componentIds.absenceDates)
      return dateRangeError[0] ?? dateRangeError[1] ?? ''
    }

    if (expectedReturnError) {
      setAlertComponentId(componentIds.expectedReturn)
      return 'Expected return is required'
    }

    if (!shiftString) {
      setAlertComponentId(componentIds.shiftString)
      return 'Shift description is required'
    }

    if (partDayAbsence) {
      if (!partDayAbsentFrom) {
        setAlertComponentId(componentIds.partDayAbsenceFrom)
        return 'Time employee left is required for part day absence'
      }

      if (
        wfmAbsence &&
        (partDayAbsentFrom > new Date(wfmAbsence.scheduleEndDate) ||
          partDayAbsentFrom < new Date(wfmAbsence.scheduleStartDate))
      ) {
        const dateFormat = `${localeDateFormat} HH:mm:SS`
        const shiftStart = format(new Date(wfmAbsence.scheduleStartDate), dateFormat)
        const shiftEnd = format(new Date(wfmAbsence.scheduleEndDate), dateFormat)

        setAlertComponentId(componentIds.partDayAbsenceFrom)
        return (
          'Time the employee left is required to be within WFM schedule provided: ' +
          `${shiftStart} and ${shiftEnd}`
        )
      }
    }

    if (!absenceReason) {
      setAlertComponentId(componentIds.absenceType)
      return 'Absence type is required'
    }

    if (!absenceType) {
      setAlertComponentId(componentIds.absenceReason)
      return 'Absence reason is required'
    }

    if (!isValidPlannerDate(contactDateTime) && contactMadeBy?.value !== undefined && contactMadeBy?.value > 0) {
      setAlertComponentId(componentIds.contactedDate)
      return 'Contacted date is required'
    }

    const isManAuthValidWithHours = isManAuthValid && managerApprovedHours

    if (isManAuthValidWithHours && comments.trim().length === 0) {
      setAlertComponentId(componentIds.comments)
      return 'Comments are required'
    }

    setAlertMessage(undefined)
    setAlertComponentId(componentIds.none)
    return undefined
  }, [
    absenceDates,
    selectedEmployee,
    shiftString,
    expectedReturn,
    partDayAbsence,
    wfmAbsence,
    absenceType,
    absenceReason,
    contactMadeBy,
    contactDateTime,
    comments,
    isManAuthValid,
    managerApprovedHours,
    expectedReturnError,
  ])

  const linkWfmPlaceholderToAbsence = (id: number) => {
    if (id === undefined) {
      return
    }

    const createWfmAbsenceLinkRequest: WFMAbsenceActionedPayload = {
      wfmAbsenceId: wfmAbsence?.id,
      absenceId: id,
      actionedBy: userInfo?.name,
    }

    return actionsService.wfm
      .linkWfmPlaceholderToAbsence(createWfmAbsenceLinkRequest)
      .then(response => {
        if (response.success) {
          dispatch(showSuccessMessage('WFM Absence has now been actioned'))
          dispatch(hideHeaderDrawer())
          dispatch(hideDrawer())
          refreshActions()
        }
      })
      .catch(err => {
        dispatch(
          showErrorMessage(
            `An error occured whilst linking WFM Placeholder absence ${wfmAbsence?.id} to absence ${createWfmAbsenceLinkRequest.absenceId}`
          )
        )
      })
  }

  const isPayApprovalConfirmationMissing = isManAuthValid && !isPayApprovalConfirmed
  const isWorkBackConfirmationMissing = shouldShowWorkBackConfirmation && !isWorkBackConfirmed
  const isSickNoteConfirmationMissing = shouldShowSickNoteReminder && !isSickNoteConfirmed
  const isNonLastSickness = (isSickness && hasFutureSicknessAbsence)

  const submit = () => {
    if (formAlertMessage) {
      setAlertMessage(formAlertMessage)
      return
    }

    if (
      !selectedEmployee ||
      !absenceType ||
      !absenceReason ||
      !selectedDays ||
      !absenceDates[0] ||
      !absenceDates[1] ||
      !expectedReturn
    ) {
      return
    }

    const createRequest: AbsenceCreateRequest = {
      absenceId: 0,
      employeeId: selectedEmployee.employeeId,
      absenceDate: format(absenceDates[0], DateFormats.DATE_AND_TIME),
      endDate: format(absenceDates[1], DateFormats.Date),
      shiftDescription: shiftString,
      absenceTypeReasonId: absenceReason.value,
      absenceTypeId: absenceType.value,
      comments:
        Boolean(wfmAbsence) && wfmAbsence?.notes
          ? `WFM Notes: ${wfmAbsence.notes} | Manager comments: ${comments}`
          : comments,
      contactDateTime: format(contactDateTime ?? new Date(), DateFormats.DATE_AND_TIME),
      contactedByTypeId: contactMadeBy?.value ?? 0,
      contactComments,
      expectedReturn: format(expectedReturn, DateFormats.Date),
      toWorkBack,
      covidIsolationIndicator: covidIndicator,
      sendFDN: sendFirstDayNotification,
      hasHadLunch,
      partDayAbsence,
      days: selectedDays.map(
        selectedDay =>
          ({
            day: format(selectedDay.day, DateFormats.Date),
            hours: selectedDay.hours,
            checked: selectedDay.checked,
          } as unknown as AbsenceDayRequestStr)
      ),
    }

    // Handle man auth days calculation
    if (isManAuthRequest && managerApprovedHours !== undefined) {
      const manAuthDays = calculateManAuthDays(
        selectedDays,
        managerApprovedHours,
        exceededHoursPerDay
      )
      
      createRequest.isManAuthRequested = true
      createRequest.manAuthDetails = {
        days: manAuthDays,
        requesterComment: comments,
      }
    }

    createRequest.wfmAbsenceId = wfmAbsence?.id

    setIsSubmitting(true)
    requestsService
      .createAbsenceRequest(createRequest, true)
      .then(data => {
        if (data.errors !== undefined && data.errors.length > 0) {
          data.errors.forEach(error => {
            dispatch(showErrorMessage(<UserErrorMessage name={error.name} />))
          })
        } else {
          return linkWfmPlaceholderToAbsence(data.absenceId)
        }
      })
      .catch(err => {
        const response: BaseResponse | undefined = err?.response?.data
        response?.errors?.forEach(error => {
          dispatch(showErrorMessage(<UserErrorMessage name={error.name} />))
        })
      })
      .finally(() => setIsSubmitting(false))
  }

  return (
    <Grid container spacing={4} ref={gridContainerRef}>
      {Boolean(alertMessage) && (
        <Grid item xs={12}>
          <Alert severity="error" message={alertMessage ?? ''} />
        </Grid>
      )}
      <Grid item xs={12} lg={8}>
        <Grid container xs={12}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Paragraph weight="bold">Absence Detail</Paragraph>
            </Grid>
            <EmployeePicker
              selectedEmployee={selectedEmployee}
              employees={directReports}
              disabled={Boolean(employeeId)}
              setSelectedEmployee={setSelectedEmployee}
              error={isComponentWithError(componentIds.employeeSelection)}
            />
            {Boolean(selectedEmployee) && (
              <>
                <AbsenceTimeFramePicker
                  absenceRange={absenceDates}
                  updateAbsenceRange={setAbsenceDates}
                  shiftString={shiftString}
                  updateShiftString={setShiftString}
                  onShiftStringBlur={() => 1}
                  expectedReturn={expectedReturn}
                  updateExpectedReturn={setExpectedReturn}
                  disableShiftStringEditing={disableShiftStringEditing}
                  disableDatesSelection={disableEditing}
                  shiftStringError={isComponentWithError(componentIds.shiftString)}
                  onAbsenceRangeError={setDateRangeError}
                  onExpectedReturnError={setExpectedReturnError}
                />
                <PartDayAbsenceForm
                  isOpen={partDayAbsence}
                  setIsOpen={setPartDayAbsence}
                  popperText="Use this option to record the time the absence started."
                  shiftDescription="Shift String"
                  dateRange={absenceDates}
                  absentFrom={partDayAbsentFrom}
                  hasHadLunch={hasHadLunch}
                  setHasHadLunch={setHasHadLunch}
                  toWorkBack={toWorkBack}
                  setToWorkBack={setToWorkBack}
                  setAbsentFromDate={newValue => {
                    setPartDayAbsentFrom(newValue)
                  }}
                  disabledPartDayAbsence={disableEditing}
                  disabledAbsentFrom={disableEditing}
                  partDayAbsenceFromError={isComponentWithError(componentIds.partDayAbsenceFrom)}
                />
                <AbsenceTypeSelection
                  absenceReasons={absenceReasonTypeList}
                  absenceTypes={absenceTypesList}
                  locationAbsenceTypes={employeeAbsenceTypesList}
                  disabled={false}
                  selectedAbsenceReason={absenceReason}
                  setAbsenceReason={setAbsenceReason}
                  selectedAbsenceType={absenceType}
                  setAbsenceType={setAbsenceType}
                  covidIsolationIndicator={covidIndicator}
                  setCovidIsolationIndicator={setCovidIndicator}
                  absenceTypeError={isComponentWithError(componentIds.absenceType)}
                  absenceReasonError={isComponentWithError(componentIds.absenceReason)}
                  selectedEmployee={selectedEmployee}
                  isWfmAbsence={Boolean(wfmAbsence)}
                />

                {isSickness && hasFutureSicknessAbsence && (
                  <Grid item xs={12}>
                    <InfoBanner
                      dataTestId="future-sickness-warning"
                      text={absenceFormLabels.futureSicknessAbsenceCreateWarning}
                    />
                  </Grid>
                )}
                
                {/* OSP Entitlement Card for Sickness */}
                {isSickness && (
                  <Grid item xs={12} md={6} lg={12}>
                    <OSPEntitlementCard
                      entitlementValue={totalSickPayAllowanceHours}
                      usedValue={
                        typeof totalSickPayAllowanceHours === 'number' &&
                        typeof totalAvailableOSP === 'number'
                          ? totalSickPayAllowanceHours - totalAvailableOSP
                          : null
                      }
                      remainingValue={totalAvailableOSP}
                      loading={isLoadingOSP}
                      exceededHours={exceededHours}
                      onApprovalHoursChange={handleApprovalHoursChange}
                      approvalHoursValue={managerApprovedHours}
                      absenceTypeId={absenceType?.value || 0}
                      isManAuthRequest={isManAuthRequest}
                    />
                  </Grid>
                )}

                {/* Non-Sickness Man Auth Components */}
                {isNonSicknessOrNoShow && showNonSicknessOspCard && (
                  <>
                    <Grid item xs={12}>
                      <OSPNotificationWithApproval
                        exceededHours={totalRequestedHours}
                        NonSicknessManAuth
                        absenceTypeId={absenceType?.value || 0}
                        employeeHandbookUrl={undefined}
                      />
                    </Grid>
                    {isManAuthRequest && (
                      <Grid item xs={12} sx={{ marginTop: '24px' }}>
                        <Box
                          sx={{
                            marginBottom: '24px',
                            width: '100%',
                          }}
                        >
                          <NumberInput
                            label="Hours for approval"
                            id="hours-for-approval"
                            size="small"
                            fullWidth
                            max={totalRequestedHours}
                            showMaxValue
                            value={managerApprovedHours ?? null}
                            onChange={handleApprovalHoursChange}
                            sx={{
                              width: '100%',
                              '& .MuiOutlinedInput-root': {
                                height: '48px',
                                width: '100%',
                              },
                              '& .MuiOutlinedInput-input': {
                                display: 'flex',
                                alignItems: 'center',
                                height: '100%',
                                padding: '0 14px',
                              },
                              '& .MuiInputLabel-outlined': {
                                transform: 'translate(14px, 14px) scale(1)',
                              },
                              '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
                                transform: 'translate(14px, -6px) scale(0.75)',
                              },
                            }}
                          />
                        </Box>
                      </Grid>
                    )}
                  </>
                )}
              </>
            )}
            <ContactMade
              contactMadeBy={contactMadeBy}
              setContactMadeBy={newValue => {
                setContactMadeBy(newValue)
              }}
              contactDateTime={contactDateTime}
              setContactDateTime={newValue => {
                setContactDateTime(newValue)
              }}
              contactMadeByError={isComponentWithError(componentIds.contactMadeBy)}
            />
            <AbsenceComments
              comments={comments}
              setComments={setComments}
              contactComments={contactComments}
              setContactComments={setContactComments}
              wfmComments={wfmComments}
              errors={{ comments: isComponentWithError(componentIds.comments) }}
            />
            <Grid item xs={12} md={12}>
              <SwitchContainer>
                <CheckBox
                  defaultChecked
                  label="Send First Day Notification"
                  name="sendFdn"
                  onChange={event => setSendFirstDayNotification(event.target.checked)}
                />
              </SwitchContainer>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid
        item
        xs={12}
        lg={4}
        pl={4}
        sx={{
              marginLeft: 'auto',
              textAlign: 'right',
              display: 'flex',
              flexDirection: 'column',
            }}
      >
        <Grid
          container
          direction="column"
          sx={{ height: { xs: 'auto', md: '100%' }, justifyContent: 'space-between' }}
        >
          <Grid item>
            <AbsenceSummary show={Boolean(selectedEmployee)} hasConflict={false} />
          </Grid>
          <Grid item sx={{ marginTop: '24px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <ConfirmationCard confirmations={confirmations} />
            </Box>
          </Grid>
        </Grid>
      </Grid>
      <SubmitActionFooter
        canSubmit={!isPayApprovalConfirmationMissing && !isWorkBackConfirmationMissing && !isSickNoteConfirmationMissing && !isNonLastSickness}
        submit={submit}
        loading={isSubmitting}
      />
    </Grid>
  )
}

export default WfmAbsenceActionDrawer
